import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';

export type SortDir = 'asc' | 'desc';

interface SortableHeaderProps {
  label: string;
  sortKey: string;
  activeSortKey: string | null;
  direction: SortDir;
  onSort: (key: string) => void;
  className?: string;
  align?: 'left' | 'right';
}

export function SortableHeader({
  label,
  sortKey,
  activeSortKey,
  direction,
  onSort,
  className,
  align = 'left',
}: SortableHeaderProps) {
  const active = activeSortKey === sortKey;
  return (
    <button
      type="button"
      className={cn(
        'flex select-none items-center gap-0.5',
        align === 'right' && 'ml-auto flex-row-reverse',
        className,
      )}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && direction === 'asc' ? (
        <ChevronUp className="h-3 w-3 shrink-0" />
      ) : active && direction === 'desc' ? (
        <ChevronDown className="h-3 w-3 shrink-0" />
      ) : (
        <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-30" />
      )}
    </button>
  );
}

export function useSortColumn<T extends object>(initialKey: keyof T | null = null) {
  const [sortKey, setSortKey] = useState<keyof T | null>(initialKey);
  const [direction, setDirection] = useState<SortDir>('asc');

  function handleSort(key: string) {
    const k = key as keyof T;
    if (sortKey === k) {
      setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setDirection('asc');
    }
  }

  function sortData<U extends T>(data: U[]): U[] {
    if (!sortKey) return data;
    const k = sortKey as keyof U;
    return [...data].sort((a, b) => {
      const av = a[k];
      const bv = b[k];
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''));
      }
      return direction === 'asc' ? cmp : -cmp;
    });
  }

  return { sortKey: sortKey as string | null, direction, handleSort, sortData };
}
